import React, { useState, useEffect } from 'react';
import { Play, Calendar, Trophy, BarChart3, ChevronRight, Dumbbell, Plus, X, Edit2, Trash2, Clock, Save, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  AreaChart, 
  Area, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import { getLatestWorkouts, getRoutines, getRoutine, getAllWorkouts, deleteWorkoutLog, updateWorkoutSession, getExercises, getExercise, getBodyStats } from '../utils/api';

const Dashboard = () => {
  const [workouts, setWorkouts] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [availableExercises, setAvailableExercises] = useState([]);
  const [actualBodyStats, setActualBodyStats] = useState([]);
  const [selectedCharts, setSelectedCharts] = useState(() => {
    const saved = localStorage.getItem('selectedCharts');
    return saved ? JSON.parse(saved) : [
      { type: 'exercise_search', value: 'bench press', label: 'Bench Press Max Weight', chartType: 'Bars' },
      { type: 'body_stat', value: 'waist', label: 'Waist Measurement', chartType: 'Bars' },
      { type: 'body_stat', value: 'body_fat', label: 'Body Fat %', chartType: 'Bars' }
    ];
  });
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editSessionData, setEditSessionData] = useState({ date: '', duration: '' });
  const [resultsData, setResultsData] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [activeSessionElapsed, setActiveSessionElapsed] = useState(0);
  const [activeSessionPaused, setActiveSessionPaused] = useState(false);
  const [activeSessionPausedAt, setActiveSessionPausedAt] = useState(null);
  const [activeSessionTotalPausedTime, setActiveSessionTotalPausedTime] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    const active = localStorage.getItem('active_session');
    if (active) {
      const sessionData = JSON.parse(active);
      setHasActiveSession(true);
      const now = Date.now();
      const pausedAt = sessionData.pausedAt || null;
      const totalPausedTime = sessionData.totalPausedTime || 0;
      const isPaused = !!sessionData.isPaused;
      
      let currentTotalPausedTime = totalPausedTime;
      if (isPaused && pausedAt) {
        currentTotalPausedTime += (now - pausedAt);
      }

      if (sessionData.globalStartTime) {
        const elapsed = Math.floor((now - sessionData.globalStartTime - currentTotalPausedTime) / 1000);
        setActiveSessionElapsed(elapsed > 0 ? elapsed : 0);
      } else {
        setActiveSessionElapsed(sessionData.globalElapsed || 0);
      }
      setActiveSessionPaused(isPaused);
      setActiveSessionPausedAt(pausedAt);
      setActiveSessionTotalPausedTime(totalPausedTime);
    } else {
      setHasActiveSession(false);
    }
  }, [selectedCharts]);

  useEffect(() => {
    let timer;
    if (hasActiveSession) {
      const active = localStorage.getItem('active_session');
      if (active) {
        const sessionData = JSON.parse(active);
        const globalStartTime = sessionData.globalStartTime;
        if (globalStartTime) {
          timer = setInterval(() => {
            const now = Date.now();
            let currentTotalPausedTime = activeSessionTotalPausedTime;
            if (activeSessionPaused && activeSessionPausedAt) {
              currentTotalPausedTime += (now - activeSessionPausedAt);
            }
            const elapsed = Math.floor((now - globalStartTime - currentTotalPausedTime) / 1000);
            setActiveSessionElapsed(elapsed > 0 ? elapsed : 0);
          }, 1000);
        }
      }
    }
    return () => clearInterval(timer);
  }, [hasActiveSession, activeSessionPaused, activeSessionPausedAt, activeSessionTotalPausedTime]);

  useEffect(() => {
    const handleStorageChange = () => {
      const active = localStorage.getItem('active_session');
      if (active) {
        const sessionData = JSON.parse(active);
        setHasActiveSession(true);
        const now = Date.now();
        const pausedAt = sessionData.pausedAt || null;
        const totalPausedTime = sessionData.totalPausedTime || 0;
        const isPaused = !!sessionData.isPaused;
        
        let currentTotalPausedTime = totalPausedTime;
        if (isPaused && pausedAt) {
          currentTotalPausedTime += (now - pausedAt);
        }

        if (sessionData.globalStartTime) {
          const elapsed = Math.floor((now - sessionData.globalStartTime - currentTotalPausedTime) / 1000);
          setActiveSessionElapsed(elapsed > 0 ? elapsed : 0);
        } else {
          setActiveSessionElapsed(sessionData.globalElapsed || 0);
        }
        setActiveSessionPaused(isPaused);
        setActiveSessionPausedAt(pausedAt);
        setActiveSessionTotalPausedTime(totalPausedTime);
      } else {
        setHasActiveSession(false);
        setActiveSessionElapsed(0);
        setActiveSessionPaused(false);
        setActiveSessionPausedAt(null);
        setActiveSessionTotalPausedTime(0);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const getMetricInfo = (activityLogConfig) => {
    let metric = 'weight';
    let label = 'Max Weight';
    let unit = 'kg';

    try {
      let config = {};
      if (typeof activityLogConfig === 'string') {
        config = JSON.parse(activityLogConfig || '{}');
      } else {
        config = activityLogConfig || {};
      }
      
      let hasWeight = false;
      let hasReps = false;
      let hasTime = false;

      if (Array.isArray(config)) {
        hasWeight = config.some(c => c.name && c.name.toLowerCase().includes('weight'));
        hasReps = config.some(c => c.name && (c.name.toLowerCase().includes('reps') || c.name.toLowerCase().includes('repetitions')));
        hasTime = config.some(c => c.name && (c.name.toLowerCase().includes('time') || c.name.toLowerCase().includes('duration')));
      } else {
        const keys = Object.keys(config).filter(k => config[k] === true || config[k] === 'true').map(k => k.toLowerCase());
        hasWeight = keys.some(k => k.includes('weight'));
        hasReps = keys.some(k => k.includes('reps') || k.includes('repetitions'));
        hasTime = keys.some(k => k.includes('time') || k.includes('duration'));

        // If no matches by substring, try exact matches (legacy/fallback)
        if (!hasWeight && !hasReps && !hasTime) {
          hasWeight = !!(config.weight || config.Weight);
          hasReps = !!(config.reps || config.Reps || config.repetitions || config.Repetitions);
          hasTime = !!(config.time || config.Time || config.duration || config.Duration);
        }
      }

      if (!hasWeight) {
        if (hasReps) {
          metric = 'reps';
          label = 'Max Reps';
          unit = 'reps';
        } else if (hasTime) {
          metric = 'time';
          label = 'Max Time';
          unit = 's';
        }
      }
    } catch (e) {
      console.error("Failed to parse activity_log_config", e);
    }
    return { metric, label, unit };
  };

  const fetchData = async () => {
    try {
      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayName = daysOfWeek[new Date().getDay()];

      const [wRes, routsRes, eRes, bRes] = await Promise.all([
        getLatestWorkouts(), 
        getRoutines(),
        getExercises(),
        getBodyStats()
      ]);
      setWorkouts(wRes.data);
      
      if (routsRes.data.length > 0) {
        const todayRoutine = routsRes.data.find(r => r.name === todayName) || routsRes.data[0];
        setRoutine(todayRoutine);
      } else {
        setRoutine(null);
      }
      
      const exercises = eRes.data;
      // Also consider data in eRes.data even if not filtered by log_count if we want to be safe, 
      // but guidelines say "only propose exercises that have at least one log entry".
      // Use loose comparison to handle strings from SQLite
      const exercisesWithLogs = (exercises || []).filter(ex => parseInt(ex.log_count) > 0);
      setAvailableExercises(exercisesWithLogs);
      
      const bodyStats = bRes.data;

      // Filter available body stats based on actual data
      const statsWithData = new Set();
      bodyStats.forEach(s => {
        if (s.measurements) {
          Object.keys(s.measurements).forEach(k => {
            if (parseFloat(s.measurements[k]) > 0) statsWithData.add(k);
          });
        }
      });
      setActualBodyStats(Array.from(statsWithData));

      const allWRes = await getAllWorkouts();
      const allWorkoutsData = allWRes.data;
      setAllWorkouts(allWorkoutsData);

      const processedResults = await Promise.all(selectedCharts.map(async (chart, index) => {
        let data = [];
        let unit = '';
        let label = chart.label;

        if (chart.type === 'exercise_search' || chart.type === 'exercise_id') {
          let exerciseId = chart.type === 'exercise_id' ? chart.value : null;
          let exBase = null;
          if (!exerciseId) {
            exBase = (exercises || []).find(e => e.title.toLowerCase().includes(chart.value.toLowerCase()));
            if (exBase) exerciseId = exBase.id;
          }

          if (exerciseId) {
            try {
              const exDetail = await getExercise(exerciseId);
              const exData = exDetail.data;
              const logs = exData.logs || [];
              
              const { metric, label: metricLabel, unit: metricUnit } = getMetricInfo(exData.activity_log_config);
              unit = metricUnit;

              // Always update label for exercises to match the current metric info
              label = `${exData.title} ${metricLabel}`;

              const byDate = {};
              logs.forEach(log => {
                const date = log.date;
                // Ensure we check all keys that might match the metric
                let value = 0;
                const logData = typeof log.data === 'string' ? JSON.parse(log.data) : log.data;

                // Extract values from logData using case-insensitive keys
                const logKeys = Object.keys(logData);
                const findVal = (terms) => {
                  const key = logKeys.find(k => terms.some(t => k.toLowerCase().includes(t)));
                  const val = key ? parseFloat(logData[key] || 0) : 0;
                  return val;
                };

                if (metric === 'weight') {
                  value = findVal(['weight']);
                } else if (metric === 'reps') {
                  value = findVal(['reps', 'repetitions']);
                } else if (metric === 'time') {
                  value = findVal(['time', 'duration', 'seconds']);
                }

                if (!byDate[date] || value > byDate[date]) {
                  byDate[date] = value;
                }
              });
              data = Object.entries(byDate)
                .map(([date, value]) => ({ date, value }))
                .sort((a, b) => new Date(a.date) - new Date(b.date));
              data = data.slice(-6);
            } catch (exErr) {
              if (exErr.response && exErr.response.status === 404) {
                // If exercise doesn't exist, we'll mark it for removal
                return { ...chart, data: [], unit, label: `${label} (Not Found)`, error: 404 };
              }
              console.error(`Error fetching/processing exercise ${exerciseId}:`, exErr);
            }
          }
        } else if (chart.type === 'body_stat') {
          data = (bodyStats || [])
            .map(s => ({ 
              date: s.date, 
              value: parseFloat(s.measurements?.[chart.value] || 0),
              unit: s.units?.[chart.value] || ''
            }))
            .filter(s => s.value > 0)
            .reverse()
            .slice(-6);
          
          const latestUnit = data.length > 0 ? data[data.length - 1].unit : '';
          unit = latestUnit || ((chart.value.toLowerCase() === 'body_fat' || chart.value.toLowerCase() === 'fat' || chart.value.toLowerCase() === 'body fat') ? '%' : '');
        } else if (chart.type === 'session_history') {
          data = (allWorkoutsData || [])
            .map(w => ({
              date: w.date,
              value: w.total_duration / 60 // in minutes
            }))
            .reverse()
            .slice(-6);
          unit = 'min';
        }

        return { ...chart, data, unit, label };
      }));

      // Filter out charts that were not found (404)
      const validResults = processedResults.filter(r => r && r.error !== 404);
      
      if (validResults.length !== selectedCharts.length) {
        // Update selectedCharts to remove invalid ones
        const newSelectedCharts = selectedCharts.filter((c, i) => {
          const res = processedResults[i];
          return res && res.error !== 404;
        });
        setSelectedCharts(newSelectedCharts);
        localStorage.setItem('selectedCharts', JSON.stringify(newSelectedCharts));
      }

      setResultsData(validResults);

    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFullHistory = async () => {
    try {
      const response = await getAllWorkouts();
      setAllWorkouts(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Failed to fetch workout history', error);
    }
  };

  const confirmDeleteAction = async () => {
    if (deleteAction.type === 'chart') {
      const updated = selectedCharts.filter((_, idx) => idx !== deleteAction.index);
      setSelectedCharts(updated);
      localStorage.setItem('selectedCharts', JSON.stringify(updated));
    } else if (deleteAction.type === 'session' || deleteAction.type === 'entry') {
      try {
        await deleteWorkoutLog(deleteAction.id, deleteAction.type);
        // Refresh history
        const response = await getAllWorkouts();
        setAllWorkouts(response.data);
        // Refresh dashboard stats
        fetchData();
      } catch (error) {
        console.error(`Failed to delete ${deleteAction.type}`, error);
      }
    } else if (deleteAction.type === 'new_session') {
      localStorage.removeItem('active_session');
      window.dispatchEvent(new Event('storage'));
      localStorage.setItem('active_session', JSON.stringify({
        routineId: routine.id,
        globalStartTime: Date.now(),
        currentIndex: 0
      }));
      window.dispatchEvent(new Event('storage'));
      navigate('/workout');
    }
    setShowConfirmDelete(false);
    setDeleteAction(null);
  };

  const handleDeleteSession = async (id, type = 'session') => {
    setDeleteAction({
      type,
      id,
      title: `Delete ${type === 'session' ? 'Session' : 'Entry'}?`,
      message: `Are you sure you want to delete this ${type === 'session' ? 'entire session' : 'entry'}? This action cannot be undone.`
    });
    setShowConfirmDelete(true);
  };

  const startEditingSession = (session) => {
    setEditingSessionId(session.global_session_id);
    setEditSessionData({
      date: session.date,
      duration: formatDuration(session.total_duration)
    });
  };

  const handleUpdateSession = async (sessionId) => {
    try {
      // Parse duration MM:SS to seconds
      const parts = editSessionData.duration.split(':');
      let totalSeconds;
      if (parts.length === 2) {
        totalSeconds = (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
      } else {
        totalSeconds = parseInt(parts[0], 10);
      }

      if (isNaN(totalSeconds)) {
        alert('Invalid duration format. Use MM:SS');
        return;
      }

      await updateWorkoutSession(sessionId, {
        date: editSessionData.date,
        total_duration: totalSeconds
      });

      setEditingSessionId(null);
      // Refresh history
      const response = await getAllWorkouts();
      setAllWorkouts(response.data);
      fetchData();
    } catch (error) {
      console.error('Failed to update session', error);
      alert('Failed to update session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00'); // Ensure it's treated as local date
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
    return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    return formatStopwatch(seconds);
  };

  const formatStopwatch = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const startSession = () => {
    const active = localStorage.getItem('active_session');
    if (active) {
      const sessionData = JSON.parse(active);
      // Check if it's the SAME routine
      if (sessionData.routineId == routine?.id) {
        if (sessionData.isPaused) {
          const now = Date.now();
          const pausedDuration = now - (sessionData.pausedAt || now);
          sessionData.totalPausedTime = (sessionData.totalPausedTime || 0) + pausedDuration;
          sessionData.isPaused = false;
          sessionData.pausedAt = null;
          localStorage.setItem('active_session', JSON.stringify(sessionData));
          window.dispatchEvent(new Event('storage'));
        }
        navigate('/workout');
      } else {
        // Different routine active!
        setDeleteAction({
          type: 'new_session',
          title: 'Start New Routine?',
          message: 'Another routine is currently active. Starting this one will delete your current progress. Are you sure?'
        });
        setShowConfirmDelete(true);
      }
    } else {
      // Starting a new session with today's routine
      if (routine) {
        localStorage.setItem('active_session', JSON.stringify({
          routineId: routine.id,
          globalStartTime: Date.now(),
          currentIndex: 0
        }));
        window.dispatchEvent(new Event('storage'));
        navigate('/workout');
      }
    }
  };
  const createRoutine = () => navigate('/routine');

  if (loading) return <Layout>Loading...</Layout>;

  const hasRoutine = routine?.sequence?.length > 0;

  return (
    <Layout title="DASHBOARD" subtitle="Welcome back">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Action Card */}
        <div className="lg:col-span-2 bg-[#1C1C1E] rounded-[40px] p-10 border border-[#2C2C2E] relative overflow-hidden flex flex-col justify-center min-h-[320px]">
          <div className="absolute top-0 right-0 p-10 opacity-5">
            <Dumbbell size={200} fill="currentColor" />
          </div>
          <div className="relative z-10">
            <h2 className="text-5xl font-black italic tracking-tighter uppercase mb-2">
              {routine?.name || 'Routine'}
            </h2>
            <p className="text-slate-500 font-bold uppercase tracking-widest mb-10">
              {routine?.sequence?.filter(s => s.type !== 'break').length || 0} Exercises Configured
            </p>
            
            <button 
              onClick={hasRoutine ? startSession : createRoutine}
              className="bg-[#A3E635] text-black px-12 py-6 rounded-full font-black text-2xl flex items-center gap-4 hover:scale-105 transition-transform shadow-[0_0_40px_rgba(163,230,53,0.3)]"
            >
              {hasRoutine ? (
                <>
                  <Play size={28} fill="black" /> {hasActiveSession ? 'CONTINUE SESSION' : 'START SESSION'}
                </>
              ) : (
                <>
                  <Plus size={28} strokeWidth={3} /> CREATE ROUTINE
                </>
              )}
            </button>

            {hasActiveSession && (
              <div className="mt-6 flex items-center gap-4 text-[#A3E635]">
                <div className="flex items-center gap-2 bg-[#A3E6351A] px-6 py-3 rounded-full border border-[#A3E63533]">
                  <Clock size={24} className={activeSessionPaused ? 'opacity-50' : 'animate-pulse'} />
                  <span className="text-3xl font-black tabular-nums tracking-wider">
                    {formatStopwatch(activeSessionElapsed)}
                  </span>
                </div>
                {activeSessionPaused && (
                  <span className="text-xs font-bold uppercase tracking-widest opacity-50">Paused</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Column */}
        <div className="space-y-6">
          <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E]">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-slate-500" />
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Last 5 Sessions</h3>
              </div>
              <button 
                onClick={fetchFullHistory}
                className="p-1 rounded-full text-slate-500 hover:text-[#A3E635] hover:bg-[#A3E6351A] transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            
            <ul className="bg-[#121214] rounded-2xl border border-[#2C2C2E] overflow-hidden">
              {workouts.length > 0 ? workouts.map((w, i) => (
                <li key={i} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#A3E635] shadow-[0_0_8px_rgba(163,230,53,0.5)]"></div>
                    <span className="font-bold text-sm tabular-nums">{formatDate(w.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[10px] font-bold tabular-nums">
                    <Clock size={10} />
                    {formatDuration(w.total_duration)}
                  </div>
                </li>
              )) : (
                <li className="text-slate-600 text-xs italic p-4 list-none text-center">No sessions logged yet.</li>
              )}
            </ul>
          </div>

          <div className="bg-[#A3E635] rounded-[32px] p-8 text-black">
            <div className="flex items-center justify-between mb-4">
              <Trophy size={32} />
              <BarChart3 size={24} className="opacity-30" />
            </div>
            <h3 className="text-2xl font-black italic tracking-tighter uppercase">Trophy case</h3>
            <p className="font-bold text-sm opacity-70">Empty, for now...</p>
            <div className="mt-6 h-2 bg-black/10 rounded-full overflow-hidden">
               <div className="h-full bg-black w-3/4"></div>
            </div>
          </div>
        </div>

      </div>

      {/* My Results Section */}
      {(() => {
        const hasWorkouts = allWorkouts && allWorkouts.length > 0;
        const hasExerciseLogs = availableExercises && availableExercises.length > 0;
        const hasBodyStats = actualBodyStats && actualBodyStats.length > 0;
        
        const showSection = hasWorkouts || hasExerciseLogs || hasBodyStats;
        
        if (!showSection) return null;

        // If data is available but no charts are selected, show a prompt to configure charts.
        if (resultsData.length === 0) {
          return (
            <div 
              id="results-section"
              className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E] mt-8 text-center"
            >
              <div className="py-12 flex flex-col items-center justify-center text-slate-500 space-y-4">
                <BarChart3 size={48} className="opacity-20" />
                <h3 className="text-lg font-black uppercase tracking-tight text-white">My Results</h3>
                <p className="italic text-sm">No charts selected to display.</p>
                <button 
                  onClick={() => setShowOptionsModal(true)}
                  className="bg-[#A3E635] text-black px-8 py-3 rounded-full font-black uppercase text-sm hover:scale-105 transition-transform mt-4"
                >
                  Configure Charts
                </button>
              </div>
            </div>
          );
        }

        return (
          <div 
            id="results-section"
            className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E] mt-8"
            style={{ display: 'block', minHeight: '100px' }}
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-lg font-black uppercase tracking-tight">My Results</h3>
              <button 
                onClick={() => setShowOptionsModal(true)}
                className="p-2 rounded-full text-slate-500 hover:text-[#A3E635] hover:bg-[#A3E6351A] transition-colors"
              >
                <Pencil size={18} />
              </button>
            </div>

            {resultsData.some(chart => chart.data && chart.data.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {resultsData.filter(chart => chart.data && chart.data.length > 0).map((chart, idx) => (
                  <div key={idx} className="space-y-4">
                    <p className="text-slate-400 font-bold text-sm">{chart.label}</p>
                    <div className="h-40 flex items-end gap-1">
                      {chart.chartType === 'Line' ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={chart.data}>
                            <defs>
                              <linearGradient id="colorAreaDashboard" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#A3E635" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#A3E635" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                            <XAxis 
                              dataKey="date" 
                              hide 
                            />
                            <YAxis 
                              hide 
                              domain={['dataMin - 5', 'dataMax + 5']}
                            />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid #2C2C2E', borderRadius: '16px' }}
                              itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
                              labelFormatter={(label) => formatDate(label)}
                              formatter={(value, name, props) => {
                                const unit = props.payload.unit || chart.unit;
                                return [`${value} ${unit}`.trim(), chart.label];
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke="none"
                              fill="url(#colorAreaDashboard)"
                              legendType="none"
                              tooltipType="none"
                              connectNulls
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#A3E635"
                              strokeWidth={3}
                              dot={{ fill: '#1C1C1E', strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6, strokeWidth: 0 }}
                              connectNulls
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        (() => {
                          const values = chart.data.map(d => d.value);
                          const max = Math.max(...values);
                          return chart.data.map((d, i) => {
                            const height = max > 0 ? (d.value / max) * 100 : 0;
                            const isLast = i === chart.data.length - 1;
                            
                            const opacity = 20 + (i * 15);
                            let bgColor = isLast ? '#A3E635' : `rgba(163, 230, 53, ${opacity / 100})`;
                            
                            if (chart.value === 'waist') {
                              bgColor = isLast ? '#A3E635' : '#2C2C2E';
                            }

                            return (
                              <div 
                                key={i} 
                                className="flex-1 rounded-t-lg transition-all duration-500" 
                                style={{ 
                                  height: `${height}%`, 
                                  backgroundColor: bgColor 
                                }}
                                title={`${d.date}: ${d.value.toFixed(1)}${chart.unit}`}
                              ></div>
                            );
                          });
                        })()
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center text-slate-500 space-y-4">
                <BarChart3 size={48} className="opacity-20" />
                <p className="italic text-sm">No data logged yet for your selected metrics.</p>
                <button 
                  onClick={() => setShowOptionsModal(true)}
                  className="text-[#A3E635] text-xs font-black uppercase tracking-widest hover:underline"
                >
                  Configure Charts
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Chart Options Modal */}
      {showOptionsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowOptionsModal(false)} />
          <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] w-full max-w-4xl max-h-[80vh] flex flex-col p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase">Chart Options</h2>
              <button onClick={() => setShowOptionsModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-hidden">
              {/* Left Column: Selected */}
              <div className="flex flex-col h-full overflow-hidden">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">Currently Selected</h4>
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                  {selectedCharts.length > 0 ? selectedCharts.map((chart, i) => (
                    <div key={i} className="bg-[#121214] border border-[#2C2C2E] rounded-2xl p-4 flex items-center justify-between group">
                      <div className="flex flex-col gap-1">
                        <span className="font-bold text-sm text-white">{chart.label}</span>
                        <select 
                          value={chart.chartType || 'Bars'}
                          onChange={(e) => {
                            const updated = [...selectedCharts];
                            updated[i] = { ...updated[i], chartType: e.target.value };
                            setSelectedCharts(updated);
                            localStorage.setItem('selectedCharts', JSON.stringify(updated));
                          }}
                          className="bg-[#1C1C1E] border border-[#2C2C2E] text-[#A3E635] text-[10px] font-black uppercase rounded-lg px-2 py-1 outline-none cursor-pointer hover:border-[#A3E635] transition-colors"
                        >
                          <option value="Bars">Bars</option>
                          <option value="Line">Line</option>
                        </select>
                      </div>
                      <button 
                        onClick={() => {
                          setDeleteAction({
                            type: 'chart',
                            index: i,
                            title: 'Remove Chart?',
                            message: 'Are you sure you want to remove this chart from your dashboard? You can add it back later from the options.'
                          });
                          setShowConfirmDelete(true);
                        }}
                        className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )) : (
                    <p className="text-slate-600 text-xs italic text-center py-8">No charts selected</p>
                  )}
                </div>
              </div>

              {/* Right Column: Available */}
              <div className="flex flex-col h-full overflow-hidden border-l border-[#2C2C2E] pl-8">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">Available Options</h4>
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-6">
                  {/* General */}
                  {allWorkouts.length > 0 && (
                    <div>
                      <h5 className="text-[9px] font-bold text-slate-600 uppercase mb-2">General</h5>
                      <button 
                        onClick={() => {
                          const newChart = { type: 'session_history', value: 'duration', label: 'Session History (Duration)', chartType: 'Bars' };
                          const updated = [...selectedCharts, newChart];
                          setSelectedCharts(updated);
                          localStorage.setItem('selectedCharts', JSON.stringify(updated));
                        }}
                        className="w-full text-left p-3 rounded-xl bg-[#121214] border border-[#2C2C2E] hover:border-[#A3E635] transition-all group mb-2"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-xs">Session History</span>
                          <Plus size={14} className="text-slate-600 group-hover:text-[#A3E635]" />
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Body Stats */}
                  {actualBodyStats.length > 0 && (
                    <div>
                      <h5 className="text-[9px] font-bold text-slate-600 uppercase mb-2">Body Measurements</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { val: 'Weight', lab: 'Weight' },
                          { val: 'Waist', lab: 'Waist' },
                          { val: 'Body Fat', lab: 'Body Fat %' },
                          { val: 'Chest', lab: 'Chest' },
                          { val: 'Arms', lab: 'Arms' },
                          { val: 'Shoulders', lab: 'Shoulders' },
                          { val: 'Legs', lab: 'Legs' }
                        ].map(stat => {
                          const actual = actualBodyStats.find(abs => abs.toLowerCase() === stat.val.toLowerCase());
                          if (!actual) return null;

                          return (
                            <button 
                              key={stat.val}
                              onClick={() => {
                                const newChart = { type: 'body_stat', value: actual, label: stat.lab, chartType: 'Bars' };
                                const updated = [...selectedCharts, newChart];
                                setSelectedCharts(updated);
                                localStorage.setItem('selectedCharts', JSON.stringify(updated));
                              }}
                              className="w-full text-left p-3 rounded-xl bg-[#121214] border border-[#2C2C2E] hover:border-[#A3E635] transition-all group"
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-xs">{stat.lab}</span>
                                <Plus size={14} className="text-slate-600 group-hover:text-[#A3E635]" />
                              </div>
                            </button>
                          );
                        })}
                        {/* Custom stats (those not in the hardcoded list above) */}
                        {actualBodyStats.filter(abs => ![
                          'Weight', 'Waist', 'Body Fat', 'Chest', 'Arms', 'Shoulders', 'Legs'
                        ].some(h => h.toLowerCase() === abs.toLowerCase())).map(customLabel => (
                          <button 
                            key={customLabel}
                            onClick={() => {
                              const newChart = { type: 'body_stat', value: customLabel, label: customLabel, chartType: 'Bars' };
                              const updated = [...selectedCharts, newChart];
                              setSelectedCharts(updated);
                              localStorage.setItem('selectedCharts', JSON.stringify(updated));
                            }}
                            className="w-full text-left p-3 rounded-xl bg-[#121214] border border-[#2C2C2E] hover:border-[#A3E635] transition-all group"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-xs">{customLabel}</span>
                              <Plus size={14} className="text-slate-600 group-hover:text-[#A3E635]" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Exercises */}
                  <div>
                    <h5 className="text-[9px] font-bold text-slate-600 uppercase mb-2">Exercises</h5>
                    <div className="grid grid-cols-1 gap-2">
                      {availableExercises.length > 0 ? (
                        availableExercises.map(ex => (
                          <button 
                            key={ex.id}
                            onClick={() => {
                              const { label: metricLabel } = getMetricInfo(ex.activity_log_config);
                              const newChart = { type: 'exercise_id', value: ex.id, label: `${ex.title} ${metricLabel}`, chartType: 'Bars' };
                              const updated = [...selectedCharts, newChart];
                              setSelectedCharts(updated);
                              localStorage.setItem('selectedCharts', JSON.stringify(updated));
                            }}
                            className="w-full text-left p-3 rounded-xl bg-[#121214] border border-[#2C2C2E] hover:border-[#A3E635] transition-all group"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-xs">{ex.title}</span>
                              <Plus size={14} className="text-slate-600 group-hover:text-[#A3E635]" />
                            </div>
                          </button>
                        ))
                      ) : (
                        <p className="text-slate-600 text-[10px] italic p-4 bg-[#121214] rounded-xl border border-[#2C2C2E]">No exercises available yet. Create some in the library!</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-[#2C2C2E] flex justify-end">
              <button 
                onClick={() => setShowOptionsModal(false)}
                className="bg-[#A3E635] text-black px-8 py-3 rounded-full font-black uppercase text-sm hover:scale-105 transition-transform"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowHistoryModal(false)} />
          <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] w-full max-w-2xl max-h-[80vh] flex flex-col p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase">Session History</h2>
              <button onClick={() => setShowHistoryModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="overflow-y-auto pr-2 custom-scrollbar">
              {allWorkouts.length > 0 ? (
                <div className="bg-[#121214] rounded-2xl border border-[#2C2C2E] overflow-hidden">
                  <table className="w-full text-left">
                    <tbody className="divide-y divide-[#2C2C2E]/50">
                      {allWorkouts.map((w, i) => (
                        <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-[#A3E635] shadow-[0_0_8px_rgba(163,230,53,0.5)]"></div>
                              {editingSessionId === w.global_session_id ? (
                                <input
                                  type="date"
                                  value={editSessionData.date}
                                  onChange={(e) => setEditSessionData({ ...editSessionData, date: e.target.value })}
                                  className="bg-[#2C2C2E] border-none rounded-lg px-2 py-1 text-sm focus:ring-1 ring-[#A3E635] outline-none text-white tabular-nums"
                                />
                              ) : (
                                <span className="font-bold text-sm tabular-nums">{w.date}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2 text-slate-400 text-xs font-bold tabular-nums">
                              <Clock size={12} className="text-slate-600" />
                              {editingSessionId === w.global_session_id ? (
                                <input
                                  type="text"
                                  value={editSessionData.duration}
                                  onChange={(e) => setEditSessionData({ ...editSessionData, duration: e.target.value })}
                                  placeholder="MM:SS"
                                  className="w-20 bg-[#2C2C2E] border-none rounded-lg px-2 py-1 text-sm focus:ring-1 ring-[#A3E635] outline-none text-white tabular-nums"
                                />
                              ) : (
                                formatDuration(w.total_duration)
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {editingSessionId === w.global_session_id ? (
                                <>
                                  <button 
                                    onClick={() => handleUpdateSession(w.global_session_id)}
                                    className="p-2 rounded-full bg-[#A3E635] text-black hover:scale-110 transition-transform"
                                    title="Save Changes"
                                  >
                                    <Save size={14} />
                                  </button>
                                  <button 
                                    onClick={() => setEditingSessionId(null)}
                                    className="p-2 rounded-full bg-[#2C2C2E] text-white hover:bg-slate-700 transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button 
                                    onClick={() => startEditingSession(w)}
                                    className="p-2 rounded-full border border-[#2C2C2E] text-slate-400 hover:text-[#A3E635] hover:border-[#A3E635] transition-all"
                                    title="Edit Session"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteSession(w.global_session_id || w.id, w.global_session_id ? 'session' : 'log')}
                                    className="p-2 rounded-full border border-red-900/50 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                                    title="Delete Session"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-600 text-xs italic py-8 text-center">No sessions logged yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={confirmDeleteAction}
        title={deleteAction?.title}
        message={deleteAction?.message}
      />
    </Layout>
  );
};

export default Dashboard;
